import {
  AnalysisGraphQL,
  GetAnalysisPipelineStep,
  GetAnalysisPipelineStepDefinition,
} from 'analysis-tool/AnalysisTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Optional} from 'util/Types';

import {AnalysisRightSidebarItem} from '../../right-sidebar/RightSidebarTypes';

// When adding a new AnalysisMainViewItemType, make sure to add it to AnalysisMainViewItemType, and one of either
// STEP_INITIALIZATION_ITEM_TYPES or STEP_AWARE_ANALYSIS_ITEM_TYPES, if necessary.
export enum AnalysisMainViewItemType {
  CHART = 'CHART',
  CHOOSE_DATASET_CREATION_METHOD = 'CHOOSE_DATASET_CREATION_METHOD',
  COMBINE_DATA_FRAMES = 'COMBINE_DATA_FRAMES',
  CONNECTION_DATA_DATASET_CONFIGURATION = 'CONNECTION_DATA_DATASET_CONFIGURATION',
  CREATE_OUTPUTS = 'CREATE_OUTPUTS',
  DATA_CATALOG_DATASET_CONFIGURATION = 'DATA_CATALOG_DATASET_CONFIGURATION',
  DATA_LAYOUT = 'DATA_LAYOUT',
  DATA_VIEW = 'DATA_VIEW',
  EMPTY_ANALYSIS = 'EMPTY_ANALYSIS',
  EDIT_CHART = 'EDIT_CHART',
  EXPLORATORY_DATA_ANALYSIS = 'EXPLORATORY_DATA_ANALYSIS',
  EXPORT_DATA_CATALOG_DATASET_OUTPUT = 'EXPORT_DATA_CATALOG_DATASET_OUTPUT',
  DATA_FRAME = 'DATA_FRAME',
  FAILED = 'FAILED',
  FILE = 'FILE',
  FILE_DASHBOARD = 'FILE_DASHBOARD',
  FLOWCHART = 'FLOWCHART',
  INSIGHTS_BLOCK_DATASET_CONFIGURATION = 'INSIGHTS_BLOCK_DATASET_CONFIGURATION',
  LOOK_UP = 'LOOK_UP',
  PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD = 'PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD',
  PARAMETER_OPTIMIZATION_MODELING_DASHBOARD = 'PARAMETER_OPTIMIZATION_MODELING_DASHBOARD',
  PARAMETER_OPTIMIZATION_MODELING_OVERVIEW = 'PARAMETER_OPTIMIZATION_MODELING_OVERVIEW',
  PARAMETER_OPTIMIZATION_PREDICTION = 'PARAMETER_OPTIMIZATION_PREDICTION',
  PLATE_SCHEMA_DATASET_CONFIGURATION = 'PLATE_SCHEMA_DATASET_CONFIGURATION',
  REGISTRY_SCHEMA_DATASET_CONFIGURATION = 'REGISTRY_SCHEMA_DATASET_CONFIGURATION',
  UPLOAD_CSV_DATASET_CONFIGURATION = 'UPLOAD_CSV_DATASET_CONFIGURATION',
  RUNNING = 'RUNNING',
  SAVE_TO_RESULTS_CONFIGURATION = 'SAVE_TO_RESULTS_CONFIGURATION',
  SAVE_TO_RESULTS_OUTPUT = 'SAVE_TO_RESULTS_OUTPUT',
  SAVE_TO_RESULTS_RECONFIGURATION = 'SAVE_TO_RESULTS_RECONFIGURATION',
  SEND_TO_DOCUMENT = 'SEND_TO_DOCUMENT',
  TEMPLATE_APPLY = 'TEMPLATE_APPLY',
  TEMPLATE_CONFIGURATION = 'TEMPLATE_CONFIGURATION',
  THIRD_PARTY_STEP = 'THIRD_PARTY_STEP',
  VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION = 'VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION',
  REGRESSION = 'REGRESSION',
  EDIT_REGRESSION = 'EDIT_REGRESSION',
}

export interface DataReference {
  stepId: string;
  stepDataName: string;
}

export interface CreationOptionalIds {
  stepIdToReplace?: string;
}

export interface ViewReference {
  stepId: string;
  outputReference: {
    stepDataName: string;
    rawDataIndex: number | null;
  } | null;
}

/**
 * TODO(BNCH-60583): Currently there is inconsistent naming for the views to (1) initially configure a step,
 * (2) view the configuration of a completed step, and (3) re-configure a step. See
 * `INSIGHTS_BLOCK_DATASET_CONFIGURATION` versus `SAVE_TO_RESULTS_CONFIGURATION`. Standardize the terminology.
 */
export type AnalysisMainViewItem =
  | {type: AnalysisMainViewItemType.CHART; stepId: string}
  | ({type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD} & CreationOptionalIds)
  | ({type: AnalysisMainViewItemType.COMBINE_DATA_FRAMES} & CreationOptionalIds)
  | ({type: AnalysisMainViewItemType.CONNECTION_DATA_DATASET_CONFIGURATION} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.CREATE_OUTPUTS}
  | ({type: AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.DATA_FRAME; stepId: string; dataFrameId: string}
  | {type: AnalysisMainViewItemType.DATA_LAYOUT; stepId: string}
  | {type: AnalysisMainViewItemType.DATA_VIEW; viewReference: ViewReference}
  // isUpdating is in the MainViewItem because we have to update it at the same time as the new viewReference
  // or there might be race conditions with out of sync analyses and viewReferences.
  | {type: AnalysisMainViewItemType.DATA_VIEW; viewReference: ViewReference; isUpdating?: true}
  | {type: AnalysisMainViewItemType.EXPLORATORY_DATA_ANALYSIS; stepId: string}
  | {type: AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT; stepId: string; dataFrameId: string}
  | {type: AnalysisMainViewItemType.FAILED; stepId: string}
  | {type: AnalysisMainViewItemType.FILE; stepId: string; fileId: string}
  | {type: AnalysisMainViewItemType.FILE_DASHBOARD; stepId: string}
  | {type: AnalysisMainViewItemType.EDIT_CHART; stepId: string}
  | {type: AnalysisMainViewItemType.EMPTY_ANALYSIS}
  | {type: AnalysisMainViewItemType.FLOWCHART}
  | ({type: AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.LOOK_UP}
  | {type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD; stepId: string}
  | {type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD; stepId: string}
  | {type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_OVERVIEW; stepId: string}
  | {type: AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_PREDICTION; stepId: string}
  | ({type: AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION} & CreationOptionalIds)
  | ({type: AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION} & CreationOptionalIds)
  | ({type: AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.RUNNING; stepId: string}
  | {
      type: AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION;
      inputDataReference: DataReference;
      inputDataFrameId: string;
    }
  | {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT; stepId: string}
  | {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION; stepId: string}
  | {type: AnalysisMainViewItemType.SEND_TO_DOCUMENT; stepId: string}
  | {type: AnalysisMainViewItemType.TEMPLATE_APPLY}
  | {type: AnalysisMainViewItemType.TEMPLATE_CONFIGURATION}
  | {type: AnalysisMainViewItemType.THIRD_PARTY_STEP; stepId: string}
  | {type: AnalysisMainViewItemType.VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION; stepId: string}
  | {type: AnalysisMainViewItemType.REGRESSION; stepId: string}
  | {type: AnalysisMainViewItemType.EDIT_REGRESSION; stepId: string};

/**
 * These view item types are views that perform configuration for the addition of step types. For example, data
 * set creation needs to be configured to specify how to create the dataset. These should not be step-aware since
 * they're always opened before adding a step.
 *
 * @package
 */
export const STEP_INITIALIZATION_ITEM_TYPES = [
  AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD,
  AnalysisMainViewItemType.COMBINE_DATA_FRAMES,
  AnalysisMainViewItemType.CONNECTION_DATA_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.LOOK_UP,
  AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION,
] as const;

export type StepInitializationAnalysisMainViewItemType = (typeof STEP_INITIALIZATION_ITEM_TYPES)[number];

export type StepInitializationAnalysisMainViewItem = AnalysisMainViewItem & {
  type: StepInitializationAnalysisMainViewItemType;
};

export const VIEW_STEP_CONFIG_ITEM_TYPES = [
  AnalysisMainViewItemType.EDIT_CHART,
  AnalysisMainViewItemType.EDIT_REGRESSION,
  AnalysisMainViewItemType.VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION,
] as const;

export type ViewStepConfigAnalysisMainViewItemType = (typeof VIEW_STEP_CONFIG_ITEM_TYPES)[number];

export const STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE: {
  [key in PipelineStepType]?: ViewStepConfigAnalysisMainViewItemType;
} = {
  [PipelineStepType.CHART_VIEW]: AnalysisMainViewItemType.EDIT_CHART,
  [PipelineStepType.TABLE_VIEW]: AnalysisMainViewItemType.EDIT_CHART,
  [PipelineStepType.REGRESSION]: AnalysisMainViewItemType.EDIT_REGRESSION,
  [PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION]:
    AnalysisMainViewItemType.VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION,
  [PipelineStepType.SAVE_TO_RESULTS]: AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION,
};

/**
 * These item types are related to a specific pipeline step and should therefore include stepId in their
 * corresponding AnalysisMainViewItem interfaces. MainView components that are step-aware will be passed the
 * GraphQL step object corresponding to the stepId defined in the AnalysisMainViewItem object.
 *
 * @package
 */
export const STEP_AWARE_ITEM_TYPES = [
  AnalysisMainViewItemType.CHART,
  AnalysisMainViewItemType.DATA_FRAME,
  AnalysisMainViewItemType.DATA_LAYOUT,
  AnalysisMainViewItemType.EDIT_CHART,
  AnalysisMainViewItemType.EXPLORATORY_DATA_ANALYSIS,
  AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT,
  AnalysisMainViewItemType.FILE,
  AnalysisMainViewItemType.FILE_DASHBOARD,
  AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD,
  AnalysisMainViewItemType.FAILED,
  AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD,
  AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_OVERVIEW,
  AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_PREDICTION,
  AnalysisMainViewItemType.RUNNING,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION,
  AnalysisMainViewItemType.SEND_TO_DOCUMENT,
  AnalysisMainViewItemType.THIRD_PARTY_STEP,
  AnalysisMainViewItemType.VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.REGRESSION,
  AnalysisMainViewItemType.EDIT_REGRESSION,
] as const;

export type StepAwareAnalysisMainViewItemType = (typeof STEP_AWARE_ITEM_TYPES)[number];

// Convenience type utility for this file to access a subset of the members of AnalysisMainViewItem based on
// the type key.
type GenericAnalysisMainViewItem<T extends AnalysisMainViewItemType> = Extract<
  AnalysisMainViewItem,
  {type: T}
>;

export function isStepInitializationAnalysisItem(
  item: AnalysisMainViewItem
): item is GenericAnalysisMainViewItem<StepInitializationAnalysisMainViewItemType> {
  return STEP_INITIALIZATION_ITEM_TYPES.includes(item.type as StepInitializationAnalysisMainViewItemType);
}

export function isViewStepConfigAnalysisItem(
  item: AnalysisMainViewItem
): item is GenericAnalysisMainViewItem<ViewStepConfigAnalysisMainViewItemType> {
  return VIEW_STEP_CONFIG_ITEM_TYPES.includes(item.type as ViewStepConfigAnalysisMainViewItemType);
}

export function isViewStepConfigEnabledForPipelineStepDefinition(
  definition: GetAnalysisPipelineStepDefinition
): boolean {
  // A `SAVE_TO_RESULTS` step can only be reconfigured if no Results were created;
  // `AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION` is accessed from within the step output view.
  if (definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
    return false;
  }
  return STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE[definition.stepType] != null;
}

export function shouldToggleSidebarWhenViewingStepConfig(stepType: PipelineStepType): boolean {
  return stepType !== PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION;
}

export function isStepAwareAnalysisItem(
  item: AnalysisMainViewItem
): item is GenericAnalysisMainViewItem<StepAwareAnalysisMainViewItemType> {
  return STEP_AWARE_ITEM_TYPES.includes(item.type as StepAwareAnalysisMainViewItemType);
}

// This will error if you have not added stepId to the AnalysisMainViewItemUnion interface for any step-aware
// main view item types.
export type _AnalysisMainViewItemsHaveStepIdsCheck =
  GenericAnalysisMainViewItem<StepAwareAnalysisMainViewItemType>['stepId'];

type StepAwareMainViewComponentProps<T extends AnalysisMainViewItemType> =
  T extends StepAwareAnalysisMainViewItemType
    ? {
        /** If T is step-aware, the MainView component is passed the pipeline step GraphQL object. */
        step: GetAnalysisPipelineStep;
      }
    : {};

export type MainViewComponentProps<T extends AnalysisMainViewItemType> =
  StepAwareMainViewComponentProps<T> & {
    analysis: AnalysisGraphQL;
    clientStateId: string;
    selectedItem: GenericAnalysisMainViewItem<T>;
    onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
    onChangeSelectedRightSidebarItem: (newSelectedItem: AnalysisRightSidebarItem | null) => void;

    // TODO(BNCH-60583): Clarify how "reconfiguration" views relate to "initialization" views
    /** Opens step initialization page and closes the sidebar */
    onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
    /** Closes step initialization page and re-opens the sidebar */
    onCloseStepInitializationView: (newSelectedItem?: Optional<AnalysisMainViewItem>) => void;
    // TODO(BNCH-43101)
    // onFinalizeDatasetConfiguration: (dataFrameId: string) => Promise<void>;
  };
